import { FC } from "react";

import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.tsx";
import { BasicWidgetHorizontal } from "src/dynamic-view-renderer/Widgets/BasicWidget/BasicWidgetHorizontal.tsx";
import { BasicWidgetVertical } from "src/dynamic-view-renderer/Widgets/BasicWidget/BasicWidgetVertical.tsx";
import { PropertyValueRendererParams } from "src/dynamic-view-renderer/Widgets/PropertyRenderer.tsx";
import { WidgetProperty } from "src/dynamic-view-renderer/Widgets/Widget.typings.ts";
import { WidgetContainerProps } from "src/dynamic-view-renderer/Widgets/WidgetContainer.tsx";
import { useFormatPropertyValue } from "src/hooks/property/useFormatProperty";
import { BlacklistedCaseIdentifier, CaseSummary } from "src/types/case";
import { ClientSchema } from "src/types/clientSchema";
import { Element } from "src/types/element";
import { PropertyValue } from "src/types/property";

import { useGetWidgetProperties } from "../WidgetProperties";
import { findTargetElement } from "../WidgetProperties/widgetProperties.utils";

export type BasicWidgetProps = {
  data?: {
    //case , customer or blacklist
    item: {
      propertyValues?: PropertyValue[];
      elements?: Element[];
      blacklistedIdentifiers?: BlacklistedCaseIdentifier[];
      customProperties?: WidgetProperty[];
      source?: CaseSummary | null;
      code: string;
      id?: string;
      type: "case" | "customer" | "blacklist";
    };
    propertyValueRendererParams?: PropertyValueRendererParams;
  };

  itemSchema?: ClientSchema;
} & Omit<WidgetContainerProps, "children" | "itemId" | "topLabel" | "elementCode">;

const DEFAULT_ORIENTATION = "vertical";

const BasicWidget: FC<BasicWidgetProps> = ({ data, itemSchema, ...widgetContainerAttributes }) => {
  const uiSchema = useDynamicViewSchema();
  const getWidgetProperties = useGetWidgetProperties();
  const formatPropertyValue = useFormatPropertyValue();

  const propertyDefinitions = uiSchema.propertyDefinitions;

  const widgetDefinition = widgetContainerAttributes.widgetDefinition;
  const propertyValueRendererParams = data?.propertyValueRendererParams;

  const orientation =
    widgetDefinition?.configuration?.orientation === "horizontal"
      ? "horizontal"
      : widgetDefinition?.configuration?.orientation === "vertical"
      ? "vertical"
      : DEFAULT_ORIENTATION;

  let widgetProperties = getWidgetProperties({
    itemSchema,
    widgetDefinition,
    data: data,
    propertyDefinitions,
  });

  const getTopLabel: () => { label: string; value: string } | undefined = () => {
    const topLabel = widgetDefinition?.configuration?.topLabel;
    if (!topLabel || !data?.item.elements) return undefined;

    const targetElement = findTargetElement(data?.item.elements, topLabel.value.elementPath);

    const properties =
      targetElement && "properties" in targetElement
        ? targetElement.properties
        : data?.item.propertyValues;
    if (!properties) return undefined;
    const propertyValue = properties.find(
      (pv) => pv.property.code === widgetDefinition.configuration?.topLabel?.value.propertyCode
    );
    if (!propertyValue) return undefined;

    return {
      label: topLabel.label,
      value: formatPropertyValue(propertyValue),
    };
  };

  const elementPath = widgetDefinition?.configuration?.elementPath;
  const elementCode = elementPath
    ? findTargetElement(data?.item.elements, elementPath)?.typeCode
    : undefined;

  // Add custom properties if they exist
  if (data?.item?.customProperties) {
    widgetProperties = [...data.item.customProperties, ...widgetProperties];
  }

  const basicWidgetAttributes = {
    properties: widgetProperties,
    propertyValueRendererParams,
    topLabel: getTopLabel(),
    elementCode: elementCode,
    itemId: data?.item.id || undefined,
    ...widgetContainerAttributes,
  };

  return orientation === "vertical" ? (
    <BasicWidgetVertical {...basicWidgetAttributes} />
  ) : (
    <BasicWidgetHorizontal {...basicWidgetAttributes} />
  );
};

export default BasicWidget;
